import { useEffect } from 'react';

/**
 * How long this client may sit in the channel with nobody else in it before the
 * session is treated as abandoned.
 *
 * Generous on purpose. The cost of firing late is some wasted recording; the
 * cost of firing early is hanging up on someone who is still on the call, so
 * the threshold sits far above any plausible wait for a late customer and far
 * below the 18-25 hour runaways this exists to prevent (CAD-6870).
 */
export const ABANDONED_AFTER_MS = 30 * 60 * 1000;

/**
 * The same for an account manager, who has a reason to hold an empty room that
 * a customer does not: the customer is late, or being called about, and the room
 * is where the appointment happens. Getting hung up on mid-wait is the likeliest
 * way this feature does damage, so their side waits twice as long — still an
 * hour against the 18-25 hour runaways it exists to prevent.
 */
export const HOST_ABANDONED_AFTER_MS = 60 * 60 * 1000;

/**
 * Ends a call that has plainly been walked away from.
 *
 * An open tab is a present participant as far as Agora is concerned, which is
 * why the server's one-sided stop cannot see this case: the channel really does
 * have a user in it. So the tab has to notice for itself and leave, which drops
 * presence and lets the recording wind up.
 *
 * The timer restarts whenever somebody is there, so only an unbroken stretch of
 * being alone counts — a customer who drops out and reconnects resets it.
 *
 * Deliberately on setTimeout rather than the requestAnimationFrame loop the
 * metrics use: rAF sleeps in a hidden tab, and a hidden tab is the commonest way
 * a call gets abandoned. This has to fire there too, so do not "unify" the two.
 *
 * Not a substitute for the server-side cap: a crashed browser, a slept laptop
 * or a dead network never runs this code at all.
 */
export default function useAbandonedSession(
  isAlone: boolean,
  onAbandoned: () => void,
  abandonedAfterMs: number = ABANDONED_AFTER_MS,
) {
  useEffect(function endSessionOnceNobodyIsLeft() {
    if (!isAlone) {
      return undefined;
    }

    const timer = setTimeout(onAbandoned, abandonedAfterMs);

    return () => clearTimeout(timer);
  }, [abandonedAfterMs, isAlone, onAbandoned]);
}
